import React, { useState, useEffect } from 'react';
import { bibliotecaDocumentosService } from '../../shared/services/bibliotecaDocumentosService';
import type { GrupoDocumento, DocumentoBiblioteca } from '../../shared/types';

export const BibliotecaPage: React.FC = () => {
  const [grupos, setGrupos] = useState<GrupoDocumento[]>([]);
  const [documentos, setDocumentos] = useState<DocumentoBiblioteca[]>([]);
  const [grupoSelecionado, setGrupoSelecionado] = useState<string | null>(null);
  const [buscaTexto, setBuscaTexto] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadGrupos();
  }, []);

  const loadGrupos = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await bibliotecaDocumentosService.getAllGrupos(false); // Usuário vê apenas ativos
      const gruposAtivos = data.filter((g) => g.ativo || (g as any).Ativo).sort((a, b) => a.ordem - b.ordem);
      setGrupos(gruposAtivos);

      // Carregar documentos do primeiro grupo automaticamente
      if (gruposAtivos.length > 0 && !grupoSelecionado) {
        await loadDocumentos(gruposAtivos[0].id);
        setGrupoSelecionado(gruposAtivos[0].id);
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar biblioteca');
      console.error('Erro ao carregar grupos:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadDocumentos = async (grupoId: string) => {
    try {
      const data = await bibliotecaDocumentosService.getDocumentos(grupoId);
      const docsAtivos = data.filter((d) => d.ativo);
      setDocumentos(docsAtivos);
    } catch (err: any) {
      console.error('Erro ao carregar documentos:', err);
      setDocumentos([]);
    }
  };

  const handleSelecionarGrupo = async (grupoId: string) => {
    setGrupoSelecionado(grupoId);
    await loadDocumentos(grupoId);
  };

  const handleBaixarDocumento = (doc: DocumentoBiblioteca) => {
    // Abrir documento em nova aba
    window.open(doc.url, '_blank');
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  const getFileIcon = (tipoArquivo: string): string => {
    if (tipoArquivo.includes('pdf')) return '📕';
    if (tipoArquivo.includes('word') || tipoArquivo.includes('document')) return '📘';
    if (tipoArquivo.includes('excel') || tipoArquivo.includes('spreadsheet')) return '📗';
    if (tipoArquivo.includes('powerpoint') || tipoArquivo.includes('presentation')) return '📙';
    if (tipoArquivo.includes('video')) return '🎥';
    if (tipoArquivo.includes('image')) return '🖼️';
    return '📄';
  };

  const documentosFiltrados = documentos.filter((doc) => {
    const textoLower = buscaTexto.toLowerCase();
    return (
      doc.titulo.toLowerCase().includes(textoLower) ||
      doc.descricao.toLowerCase().includes(textoLower) ||
      doc.autor.toLowerCase().includes(textoLower) ||
      doc.tags.some((tag) => tag.toLowerCase().includes(textoLower))
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando biblioteca...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <h3 className="text-red-800 font-semibold mb-2">Erro ao carregar biblioteca</h3>
          <p className="text-red-600">{error}</p>
          <button
            onClick={loadGrupos}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  if (grupos.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-6xl mb-4">📚</div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">
            Nenhum documento disponível
          </h3>
          <p className="text-gray-600">Não há documentos autorizados para sua empresa no momento.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg">
        <div className="container mx-auto px-4 py-8">
          <h1 className="text-3xl font-bold mb-2">📚 Biblioteca de Documentos</h1>
          <p className="text-blue-100">
            Acesse manuais técnicos, procedimentos e documentação autorizada
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar - Grupos */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-4">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Categorias</h2>
              <div className="space-y-2">
                {grupos.map((grupo) => (
                  <button
                    key={grupo.id}
                    onClick={() => handleSelecionarGrupo(grupo.id)}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-all ${
                      grupoSelecionado === grupo.id
                        ? 'bg-blue-600 text-white shadow-lg'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{grupo.icone}</span>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold truncate">{grupo.nome}</p>
                        <p className="text-xs opacity-75">
                          {grupo.totalDocumentos || 0} documento(s)
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Conteúdo Principal - Documentos */}
          <div className="lg:col-span-3">
            {/* Busca */}
            <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Buscar por título, descrição, autor ou tags..."
                  value={buscaTexto}
                  onChange={(e) => setBuscaTexto(e.target.value)}
                  className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <span className="absolute left-4 top-3.5 text-xl">🔍</span>
              </div>
            </div>

            {/* Lista de Documentos */}
            {documentosFiltrados.length === 0 ? (
              <div className="bg-white rounded-lg shadow-lg p-12 text-center">
                <div className="text-6xl mb-4">📄</div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  {buscaTexto ? 'Nenhum documento encontrado' : 'Nenhum documento nesta categoria'}
                </h3>
                <p className="text-gray-600">
                  {buscaTexto
                    ? 'Tente ajustar os termos de busca'
                    : 'Esta categoria ainda não possui documentos'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {documentosFiltrados.map((doc) => (
                  <div
                    key={doc.id}
                    className="bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow overflow-hidden"
                  >
                    <div className="p-6">
                      <div className="flex gap-6">
                        {/* Ícone do Tipo de Arquivo */}
                        <div className="flex-shrink-0">
                          <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center text-3xl">
                            {getFileIcon(doc.tipoArquivo)}
                          </div>
                        </div>

                        {/* Informações do Documento */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-4 mb-2">
                            <h3 className="text-lg font-bold text-gray-900">{doc.titulo}</h3>
                            <span className="flex-shrink-0 px-3 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full">
                              v{doc.versao}
                            </span>
                          </div>

                          <p className="text-gray-600 text-sm mb-3 line-clamp-2">{doc.descricao}</p>

                          {/* Meta Informações */}
                          <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-3">
                            <div className="flex items-center gap-1">
                              <span>👤</span>
                              <span>{doc.autor}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <span>📦</span>
                              <span>{formatFileSize(doc.tamanhoBytes)}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <span>📅</span>
                              <span>{new Date(doc.dataCriacao).toLocaleDateString('pt-BR')}</span>
                            </div>
                          </div>

                          {/* Tags */}
                          {doc.tags.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-4">
                              {doc.tags.map((tag, index) => (
                                <span
                                  key={index}
                                  className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
                                >
                                  #{tag}
                                </span>
                              ))}
                            </div>
                          )}

                          {/* Ações */}
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleBaixarDocumento(doc)}
                              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold flex items-center gap-2"
                            >
                              <span>👁️</span>
                              Visualizar
                            </button>
                            <button
                              onClick={() => handleBaixarDocumento(doc)}
                              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold flex items-center gap-2"
                            >
                              <span>⬇️</span>
                              Baixar
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Footer com Stats */}
            {documentosFiltrados.length > 0 && (
              <div className="mt-6 bg-white rounded-lg shadow-lg p-4">
                <p className="text-sm text-gray-600 text-center">
                  Exibindo <span className="font-semibold">{documentosFiltrados.length}</span> de{' '}
                  <span className="font-semibold">{documentos.length}</span> documentos
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
